import Head from 'next/head';
import { ReactElement } from 'react';
import { BlogItemFeatured, BlogItemList } from '../../components/blog';
import { IBlogItemFeaturedProps } from '../../components/blog/BlogItemFeatured';
import { ICookiesPopupProps } from '../../components/common/CookiesPopUp';
import { IFooterProps } from '../../components/common/layout/Footer';
import Layout from '../../components/common/layout/Layout';
import { INavBarProps } from '../../components/common/layout/NavBar';
import { PageJSONLDSchema } from '../../models/app.models';
import {
  IBlogPage,
  IBlogPost,
  IBlogPostsResult,
  ICustomerReviewsMeta,
  ILayoutPageProps,
  IMetaComponent,
} from '../../models/strapi.models';
import { fetchStrapi } from '../../services/api.service';
import { getRoutes } from '../../shared/useBuild';
import {
  buildApiKey,
  getCommonTranslations,
  populateCookiesPopUpProps,
  populateFooterProps,
  populateNavBarProps,
} from '../../shared/useCommon';
import {
  generateOrganizationSchema,
  generateWebsiteSchema,
} from '../../shared/useJSONLDSchema';
import {
  getAlternateLinks,
  getCanonicalLink,
  getContentLanguageCode,
  getTranslation,
} from '../../shared/useUtils';
import { IAppPage } from '../../store/slices/appSlice';
import { BlogStyle } from '../../styles/pages/blog';
import { NextPageWithLayout } from '../_app';

interface IBlogPageProps extends ILayoutPageProps {
  posts: IBlogPost[];
  meta: IMetaComponent;
  appPages: IAppPage[];
  alternativeLinks: { hrefLang: string; href: string }[];
  canonicalLink: string;
}

const BlogPage: NextPageWithLayout<IBlogPageProps> = (
  props: IBlogPageProps
) => {
  const appPages = props.appPages;
  const featuredPosts: IBlogItemFeaturedProps[] = props.posts
    .filter((post: IBlogPost) => !!post.isFeatured)
    .map((post: IBlogPost) => ({
      ...post,
      appPages,
    }));

  const regularPosts: IBlogItemFeaturedProps[] = props.posts
    .filter((post: IBlogPost) => !post.isFeatured)
    .map((post: IBlogPost) => ({
      ...post,
      appPages,
    }));

  return (
    <div className="page-container">
      <Head>
        <title>{props.meta.title}</title>
        <meta name="description" content={props.meta.description} />
        <meta name="keywords" content={props.meta.keywords} />
        <link rel="canonical" href={props.canonicalLink} />
        {props.alternativeLinks.map((x, i) => (
          <link key={i} rel="alternate" hrefLang={x.hrefLang} href={x.href} />
        ))}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(props.pageSchema.nodes),
          }}
        />
      </Head>

      <div className="page-content-section">
        <div className="page-content-block">
          <div className={BlogStyle.blog}>
            <div className={BlogStyle.featuredPost}>
              {featuredPosts.length > 0 ? (
                <BlogItemFeatured {...featuredPosts[0]} />
              ) : null}
            </div>
            <div className={BlogStyle.listPosts}>
              {regularPosts.map((data: any, index: number) => {
                return <BlogItemList key={index} {...data} />;
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

BlogPage.getLayout = function getLayout(page: ReactElement) {
  return (
    <Layout
      locale={page.props.locale}
      navBarProps={page.props.navBarProps}
      cookiesPopUpProps={page.props.cookiesPopUpProps}
      footerProps={page.props.footerProps}
      contentLanguageCode={getContentLanguageCode(page.props.locale)}>
      {page}
    </Layout>
  );
};

export async function getStaticProps(
  context: any
): Promise<{ props: IBlogPageProps }> {
  const appRoutes = (await getRoutes()).filter((x) =>
    x.original?.includes('/blog')
  );

  const pageUrlParams = new URLSearchParams({
    'populate[0]': 'meta',
  });

  const page = (
    await fetchStrapi<any>(`/blog-page`, pageUrlParams, buildApiKey)
  ).data.attributes as IBlogPage;

  const blogPostsUrlParams = new URLSearchParams({
    locale: context.locale,
    sort: `${page.sortByPropertyName}:${page.sortOrder}`,
    'fields[0]': 'id',
    'fields[1]': 'title',
    'fields[2]': 'slug',
    'fields[3]': 'publicationDate',
    'fields[4]': 'metaDescription',
    'fields[5]': 'isFeatured',
    'fields[6]': 'locale',
    'pagination[start]': '0',
    'pagination[limit]': '-1',
    disableLocalizations: 'true',
  });
  const blogPostsResponse: IBlogPostsResult =
    await fetchStrapi<IBlogPostsResult>(
      `/blog-posts/published`,
      blogPostsUrlParams,
      buildApiKey
    );
  const navBarProps: INavBarProps = await populateNavBarProps(context.locale);
  const cookiesPopUpProps: ICookiesPopupProps = await populateCookiesPopUpProps(
    context.locale
  );
  const footerProps: IFooterProps = await populateFooterProps(context.locale);
  const meta = getTranslation(page.meta, context.locale);
  const alternativeLinks = getAlternateLinks('/blog', appRoutes);
  const canonicalLink = getCanonicalLink(context.locale || 'en', meta.slug);
  const posts = blogPostsResponse.results.map((x) => {
    if (x.image) {
      x.image.formats = {
        small: x.image.formats?.medium,
      };
    }
    return x;
  });

  const commonTranslations = await getCommonTranslations();

  navBarProps.breadcrumbsHomePageDisplayName = getTranslation(
    commonTranslations.homePageName,
    context.locale
  ).text as string;
  // TODO: change in the future to use the translation from Strapi
  navBarProps.breadcrumbsCurrentPageDisplayName = 'Blog';

  const websiteSchema = generateWebsiteSchema(
    `https://www.igerent.com${
      context.locale !== 'en' ? `/${context.locale}` : ''
    }`,
    getTranslation(commonTranslations.organizationDescription, context.locale)
      .text as string
  );
  const customerReviewsMeta = (
    await fetchStrapi<any>(`/customer-reviews-meta`, undefined, buildApiKey)
  ).data.attributes as ICustomerReviewsMeta;
  const organizationSchema = await generateOrganizationSchema(
    `https://www.igerent.com${
      context.locale !== 'en' ? `/${context.locale}` : ''
    }`,
    getTranslation(commonTranslations.organizationDescription, context.locale)
      .text as string,
    customerReviewsMeta
  );
  const pageSchema: PageJSONLDSchema = {
    nodes: [...websiteSchema, ...organizationSchema],
  };

  const props = {
    // Layout
    pageSchema,
    navBarProps,
    cookiesPopUpProps,
    footerProps,
    // Page
    locale: context.locale,
    meta,
    posts,
    appPages: appRoutes.filter((x) => x.language === context.locale),
    alternativeLinks,
    canonicalLink,
  };

  return {
    props,
  };
}

export default BlogPage;
